import {
  type AdminProfileQuestion,
  type BulkQuestionStatusRequest,
  type CreateProfileQuestionRequest,
  type MoveQuestionRequest,
  type Paginated,
  type ProfileQuestionDetail,
  type ProfileQuestionsListQuerySchema,
  type QuestionBulkResult,
  type SectionVisibilityDefaultsRequest,
  type UpdateProfileQuestionRequest,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

import { bulkResultTags } from '../../utils/question-tags/question-tags.util';

type QuestionsParams = z.input<typeof ProfileQuestionsListQuerySchema>;

/**
 * The profile questions themselves.
 *
 * `key` and `type` are absent from the update contract by construction — the key is
 * the answer's address and the type is the shape every stored answer already has —
 * so the edit form omits them rather than sending values the api would refuse.
 */
export const profileQuestionsApi = createApi({
  reducerPath: 'profileQuestionsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Question', 'QuestionList'],
  endpoints: (builder) => ({
    listQuestions: builder.query<Paginated<AdminProfileQuestion>, QuestionsParams>({
      query: (params) => ({ url: '/admin/questions', method: 'GET', params }),
      providesTags: ['QuestionList'],
    }),

    questionDetail: builder.query<ProfileQuestionDetail, string>({
      query: (id) => ({ url: `/admin/questions/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Question' as const, id }],
    }),

    createQuestion: builder.mutation<ProfileQuestionDetail, CreateProfileQuestionRequest>({
      query: (body) => ({ url: '/admin/questions', method: 'POST', body }),
      invalidatesTags: ['QuestionList'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.question.changed', questionId: data.id });
        } catch {
          // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
          // has no caller, so a rejection escaping it becomes an unhandled
          // rejection. The failure is already the caller's, through `unwrap()`.
        }
      },
    }),

    updateQuestion: builder.mutation<
      ProfileQuestionDetail,
      { id: string; body: UpdateProfileQuestionRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/questions/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => ['QuestionList', { type: 'Question' as const, id }],
      async onQueryStarted({ id }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.question.changed', questionId: id });
        } catch {
          // See `createQuestion`.
        }
      },
    }),

    deleteQuestion: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/questions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['QuestionList'],
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.question.changed', questionId: id });
        } catch {
          // See `createQuestion`.
        }
      },
    }),

    /**
     * One question, placed after a named neighbour.
     *
     * The **only** way this client reorders questions, since P10.6c made the
     * pane paged: a page of twenty cannot name five hundred ids, and the api
     * refuses a partial list outright. The whole-set `PUT /reorder/:sectionId`
     * still exists on the api and is still right for a caller that holds the
     * whole set — the sections rail and the options list are exactly that, and
     * each has its own endpoint in its own api file — but nothing here needs it,
     * so no mutation is declared for it. A dead endpoint in a client is a
     * capability the next reader believes is in use (P10.1 retired `ScrollArea`
     * on the same test).
     */
    moveQuestion: builder.mutation<AdminProfileQuestion, { id: string; body: MoveQuestionRequest }>(
      {
        query: ({ id, body }) => ({ url: `/admin/questions/${id}/position`, method: 'PUT', body }),
        invalidatesTags: (_r, _e, { id }) => ['QuestionList', { type: 'Question' as const, id }],
        async onQueryStarted({ id }, { queryFulfilled }) {
          try {
            await queryFulfilled;
            EVENT_BUS.publish({ type: 'questions.question.changed', questionId: id });
          } catch {
            // See `createQuestion`.
          }
        },
      },
    ),

    /**
     * A set of questions switched on or off in one transaction.
     *
     * Answers `{ matched, changedIds }` rather than rows: the pane invalidates
     * its page tag and refetches, so returning the written rows would buy an
     * options read and an answer-count query per row for data it discards.
     *
     * It invalidates the list **and every question it changed** — it dropped the
     * list alone until P10.6f2, which left an editor already open on one of up
     * to 500 switched questions showing the old status indefinitely.
     */
    setQuestionsStatus: builder.mutation<QuestionBulkResult, BulkQuestionStatusRequest>({
      query: (body) => ({ url: '/admin/questions/status', method: 'PUT', body }),
      invalidatesTags: (result) => bulkResultTags(result),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // No single question changed, so the event carries none — the shape
          // every write over a SET uses.
          EVENT_BUS.publish({ type: 'questions.question.changed', questionId: null });
        } catch {
          // See `createQuestion`.
        }
      },
    }),

    /**
     * Visibility defaults applied to every question of one section.
     *
     * A **patch**, not a policy: the api takes the four-column policy
     * `.partial()` with at least one facet required, so applying one decision
     * across a section cannot overwrite three nobody named. Shipped by P10.6b
     * and unreachable from the portal until now.
     *
     * It invalidates the LIST **and every question it actually moved**, through
     * the same `bulkResultTags` as the status write above. An editor open on one
     * of these questions would otherwise go on showing a visibility policy the
     * database no longer holds, and this write is precisely the one that changes
     * what is already exposed about members who answered long ago.
     */
    applySectionVisibilityDefaults: builder.mutation<
      QuestionBulkResult,
      { sectionId: string; body: SectionVisibilityDefaultsRequest }
    >({
      query: ({ sectionId, body }) => ({
        url: `/admin/questions/visibility/${sectionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) => bulkResultTags(result),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // No single question changed, so the event carries none — the shape
          // every write over a SET uses, and what the api itself announces.
          EVENT_BUS.publish({ type: 'questions.question.changed', questionId: null });
        } catch {
          // See `createQuestion`.
        }
      },
    }),
  }),
});

export const {
  useApplySectionVisibilityDefaultsMutation,
  useListQuestionsQuery,
  useQuestionDetailQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useMoveQuestionMutation,
  useSetQuestionsStatusMutation,
} = profileQuestionsApi;
